import {DocumentNode, gql} from '@apollo/client/core';
import {GroupVoucherFragment} from '../../fragments/group-voucher.fragment';
import {UserCartFragment} from '../../fragments/user-cart.fragment';
import {HeadDataFragment} from '../../fragments/head-data.fragment';
import {ProductPageFragment} from '../../fragments/product-page.fragment';
import {ListPageFragment} from '../../fragments/list-page.fragment';
import {RedirectObjectFragment, ShippingMethodFragment} from '../../fragments/checkout.fragment';
import {VismaOrderDetails} from '../../fragments/visma-order-details';
import {SessionResponseFragment} from '../../fragments/session-response.fragment';
import {ProductDataFragment} from '../../fragments/product-data.fragment';
import {VismaOrderDetailsV2} from '@core/fragments/visma-order-details-v2';
import {CustomerProfileFragment} from '@core/fragments/customer-profile.fragment';

const superpayQueries: Record<string, () => DocumentNode> = {
  validateAndAdjustOrderSuperpayMutation: () => gql`
    mutation validateAndAdjustOrder($cartId: String!, $domain: String!, $uid: String!) {
      validateAndAdjustOrder(cartId: $cartId, domain: $domain, uid: $uid) {
        cart {
          id
        }
      }
    }
  `,
}

const queries: Record<string, () => DocumentNode> = {
  getCitiesByZipcodeQuery: () => gql`
    query($countryCode: String!, $zipcode: String!) {
      getCityByZipcode(countryCode: $countryCode, zipcode: $zipcode)
    }
  `,
  getMagentoOrderQuery: () => gql`
    query($id: ID!) {
      getMagentoOrder(id: $id) {
        id
        total
        customerUUID
        totalVismaFormat
        taxVismaFormat
        shippingPriceVismaFormat
        webOrderId
        isSuperpay
        lineItems {
          id
          productName
          sku
          quantity
          totalPriceVismaFormat
          qtyPriceVismaFormat
          compareAtPriceVismaFormat
        }
        couponCode
      }
    }
  `,
  getVismaOrderHistoryQuery: () => gql`
    query($pageSize: Int, $pageNumber: Int) {
      getVismaOrderHistory(pageSize: $pageSize, pageNumber: $pageNumber) {
        items {
          id
          webOrderId
          orderStatus
          totalWithVat
          createdAt
          vismaOrderNumber
        }
        totalPages
        totalItems
        page
      }
    }
  `,
  getVismaOrderHistoryV2Query: () => gql`
    query($pageSize: Int, $pageNumber: Int) {
      getVismaOrderHistoryV2(pageSize: $pageSize, pageNumber: $pageNumber) {
        items {
          id
          visualOrderId
          vismaOrderNumber
          orderStatus
          orderDate
          orderTotal
          storeName
        }
        totalPages
        totalItems
        page
      }
    }
  `,
  getVismaOrderDetailsQuery: () => gql`
    query($vismaOrderNumber: Int) {
      getVismaOrderDetails(vismaOrderNumber: $vismaOrderNumber) {
        ...vismaOrderDetail
      }
    }
    ${VismaOrderDetails}
  `,
  getVismaOrderDetailsV2Query: () => gql`
    query($vismaOrderNumber: Int!) {
      getVismaOrderDetailsV2(vismaOrderNumber: $vismaOrderNumber) {
        ...vismaOrderDetailV2
      }
    }
    ${VismaOrderDetailsV2}
  `,
  sendRpTokenMutation: () => gql`
    mutation($email: String!, $type: String!) {
      sendRpTokenOnEmail(email: $email, type: $type) {
        message
        type
      }
    }
  `,
  saveRRWebMutation: () => gql`
    mutation($events: String!, $batchId: Int!, $hash_map: String!) {
      saveRRWeb(events: $events, batch_id: $batchId, hash_map: $hash_map)
    }
  `,
  getPaymentMethodsQuery: () => gql`
    query {
      getPaymentMethods {
        id
        title
        method
        description
        icons {
          id
          original
        }
      }
    }
  `,
  placeOrderMutation: () => gql`
    mutation($paymentMethod: String!, $newsletter: Boolean, $groupVoucherNewsletter: Boolean, $supervinEventPermission: Boolean) {
      placeOrder(paymentMethod: $paymentMethod, newsletter: $newsletter, groupVoucherNewsletter: $groupVoucherNewsletter, supervinEventPermission: $supervinEventPermission) {
        ...redirectObject
      }
    }
    ${RedirectObjectFragment}
  `,
  saveCustomerNoteMutation: () => gql`
    mutation($customerNote: String!) {
      saveCustomerNote(customerNote: $customerNote)
    }
  `,
  saveShippingInfoMutation: () => gql`
    mutation($data: InputUpdateCart!) {
      saveShippingInfo(data: $data) {
        ...userCart
      }
    }
    ${UserCartFragment}
  `,
  saveShippingMethodMutation: () => gql`
    mutation($shippingMethod: String!, $deliveryComment: String, $deliveryDate: DateTime) {
      saveShippingMethod(shippingMethod: $shippingMethod, deliveryComment: $deliveryComment, deliveryDate: $deliveryDate) {
        ...userCart
      }
    }
    ${UserCartFragment}
  `,
  getProductStockQuery: () => gql`
    query getProductByVismaId($id: ID!) {
      getProductByVismaId(id: $id) {
        stock
        baPercentage
      }
    }
  `,
  getProductByVismaId: () => gql`
    query getProductByVismaId($id: ID!) {
      getProductByVismaId(id: $id) {
        ...productData
      }
    }
    ${ProductDataFragment}
  `,
  getShippingMethodsQuery: () => gql`
    query {
      getShippingMethods {
        ...shippingMethod
      }
    }
    ${ShippingMethodFragment}
  `,
  getTopSellingProductsQuery: () => gql`
    query getTopSellingProducts {
      getTopSellingProducts {
        ...productData
      }
    }
    ${ProductDataFragment}
  `,
  applyGroupVoucherMutation: () => gql`
    mutation($code: String!) {
      applyGroupVoucher(groupVoucherCode: $code) {
        ...SessionResponseFragment
      }
    }
    ${SessionResponseFragment}
  `,
  removeGroupVoucherFromCartMutation: () => gql`
    mutation {
      removeGroupVoucherFromCart {
        ...SessionResponseFragment
      }
    }
    ${SessionResponseFragment}
  `,
  registerMutation: () => gql`
    mutation($email: String!, $fullName: String!, $password: String, $phone: String) {
      register(emailForOrderConfirmation: $email, fullName: $fullName, password: $password, phone: $phone) {
        id
        authToken
        sessionResponse {
          ...SessionResponseFragment
        }
      }
    }
    ${SessionResponseFragment}
  `,
  confirmAccountMutation: () => gql`
    mutation($email: String!, $confirmationKey: String!) {
      confirmAccount(email: $email, confirmationKey: $confirmationKey) {
        id
        authToken
        sessionResponse {
          ...SessionResponseFragment
        }
      }
    }
    ${SessionResponseFragment}
  `,
  sendAccountConfirmationEmailMutation: () => gql`
    mutation($email: String!) {
      sendAccountConfirmationEmail(email: $email) {
        message
        type
      }
    }
  `,
  getSessionQuery: () => gql`
    query getSession($sessionSplitTests: String) {
      getSession(sessionSplitTests: $sessionSplitTests) {
        ...SessionResponseFragment
      }
    }
    ${SessionResponseFragment}
  `,
  updateSessionWithCustomerTokenMutation: () => gql`
    mutation updateSessionWithCustomerToken($customerToken: String, $cartId: ID, $mergeCart: Boolean) {
      updateSessionWithCustomerToken(customerToken: $customerToken cartId: $cartId, mergeCart: $mergeCart) {
        ...SessionResponseFragment
      }
    }
    ${SessionResponseFragment}
  `,
  getCustomerUUIDByEmailQuery: () => gql`
    query($email: String!) {
      getCustomerUuidByEmail(email: $email)
    }
  `,
  getCustomerByUuid: () => gql`
    query($uuid: ID!) {
      getCustomerByUuid(uuid: $uuid) {
          ...customerProfile
      }
    }
    ${CustomerProfileFragment}
  `,
  isRpTokenValidQuery: () => gql`
    query($email: String!, $resetToken: String!) {
      isRpTokenValid(email: $email, resetToken: $resetToken)
    }
  `,
  signOutMutation: () => gql`
    mutation {
      signOutV2 {
        ...SessionResponseFragment
      }
    }
    ${SessionResponseFragment}
  `,
  updateCustomerMutation: () => gql`
    mutation($data: CustomerUpsertV2Input!) {
      updateCustomer(data: $data) {
        ...customerProfile
      }
    }
    ${CustomerProfileFragment}
  `,
  updateSessionMutation: () => gql`
    mutation($cartId: ID) {
      updateSession(cartId: $cartId) {
        ...SessionResponseFragment
      }
    }
    ${SessionResponseFragment}
  `,
  applyFlexVoucherMutation: () => gql`
    mutation($code: String!) {
      applyFlexVoucher(flexVoucherCode: $code) {
        ...userCart
      }
    }
    ${UserCartFragment}
  `,
  applyCouponCodeMutation: () => gql`
    mutation($code: String!) {
      applyCouponCode(couponCode: $code) {
        ...userCart
      }
    }
    ${UserCartFragment}
  `,
  removeFlexVoucherFromCartMutation: () => gql`
    mutation($code: String!) {
      removeFlexVoucherFromCart(flexVoucherCode: $code) {
        ...userCart
      }
    }
    ${UserCartFragment}
  `,
  removeCouponCodeFromCartMutation: () => gql`
    mutation {
      removeCouponCodeFromCart {
        ...userCart
      }
    }
    ${UserCartFragment}
  `,
  updateHubspotDealMutation: () => gql`
    mutation updateHubspotDeal($dealStageStep: Int!) {
      updateHubspotDeal(dealStageStep: $dealStageStep)
    }
  `,
  authenticateV2Mutation: () => gql`
    mutation($email: String!, $password: String!) {
      authenticateV2(email: $email, password: $password) {
        id
        authToken
        sessionResponse {
          ...SessionResponseFragment
        }
      }
    }
    ${SessionResponseFragment}
  `,
  changePasswordMutation: () => gql`
    mutation($newPassword: String!, $email: String, $resetToken: String) {
      changePassword(newPassword: $newPassword, email: $email, resetToken: $resetToken) {
        message
        type
      }
    }
  `,
  getStoryblokPreviewTokenQuery: () => gql`
    query($space_id: String, $token: String, $timestamp: Int) {
      getStoryblokPreviewToken(space_id: $space_id, token: $token, timestamp: $timestamp) {
        accessToken
      }
    }
  `,
  getCartQuery: () => gql`
    query {
      getCart {
        ...userCart
      }
    }
    ${UserCartFragment}
  `,
  addToCartMutation: () => gql`
    mutation addToCart($vismaId: String, $productId: Int!, $qty: Int!, $customPrice: Int, $eventSourceName: String, $hitPosition: Int) {
      addToCart(vismaId: $vismaId, productId: $productId, qty: $qty, customPrice: $customPrice, eventSourceName: $eventSourceName, hitPosition: $hitPosition) {
        message
        cart {
          ...userCart
        }
      }
    }
    ${UserCartFragment}
  `,
  updateCartMutation: () => gql`
    mutation($sku: String!, $qty: Int!) {
      updateCart(sku: $sku, qty: $qty) {
        message
        cart {
          ...userCart
        }
      }
    }
    ${UserCartFragment}
  `,
  trackingEventMutation: () => gql`
    mutation trackingEvent($event: String!, $data: String!) {
      trackingEvent(event: $event, data: $data)
    }
  `,
  trackPurchaseEventMutation: () => gql`
    mutation($maskedCartId: ID!) {
      trackPurchaseEvent(maskedCartId: $maskedCartId)
    }
  `,
  removeFromCartMutation: () => gql`
    mutation($sku: String!) {
      removeFromCart(sku: $sku) {
        ...userCart
      }
    }
    ${UserCartFragment}
  `,
  hubspotFormEventMutation: () => gql`
    mutation($formName: String!, $data: String!) {
      hubSpotFormEvent(formName: $formName, data: $data)
    }
  `,
  getProductsByVismaIds: () => gql`
    query getProductsByVismaIds($ids: [ID]!) {
      getProductsByVismaIds(ids: $ids) {
        ...productData
      }
    }
    ${ProductDataFragment}
  `,
  getAlgoliaFiltersAndSorts: () => gql`
    query getAlgoliaFiltersAndSorts {
      getAlgoliaFiltersAndSorts {
        filters {
          id
          algoliaAttribute
          label
          instantSearchType
          hidden
        }
        sorts {
          id
          title
          indexName
        }
      }
    }
  `,
  getStoreConfig: () => gql`
    query getStoreConfig {
      getStoreConfig {
        id
        algoliaReadonlyApiKey
      }
    }
  `,
  getWineFestivalEventQuery: () => gql`
    query getWineFestivalEvent($urlKey: String!) {
      getWineFestivalEvent(urlKey: $urlKey) {
        id
        urlKey
        name
        discountPagePath
        address
        startDate
        endDate
        isActive
        mapImage
        algoliaIndex
        image
        tables {
          id
          name
          country
          countryIcon
          number
        }
      }
    }
  `,
  getWineFestivalTableQuery: () => gql`
    query getWineFestivalTable($eventUrlKey: String!, $tableNumber: Int!) {
      getWineFestivalTable(eventUrlKey: $eventUrlKey, tableNumber: $tableNumber) {
        id
        name
        country
        countryIcon
        number
        producer {
          id
          name
          image
          description
          urlKey
        }
        products {
          id
          number
          product {
            ...productData
          }
        }
      }
    }
    ${ProductDataFragment}
  `,

  getWishlistQuery: () => gql`
    query {
      getWishlist {
        id
        productId
      }
    }
  `,

  getCustomerReviewsQuery: () => gql`
    query {
      getCustomerReviews {
        rating
        product {
          id
          productId
        }
      }
    }
  `,

  adjustCustomerReviewMutation: () => gql`
    mutation($productId: Int!, $rating: Int!, $removeReview: Boolean) {
      adjustCustomerReview(productId: $productId, rating: $rating, removeReview: $removeReview)
    }
  `,

  adjustWishlistMutation: () => gql`
    mutation($productId: Int!, $removeFromWishlist: Boolean) {
      adjustWishlist(productId: $productId, removeFromWishlist: $removeFromWishlist)
    }
  `,

  getPageInfo: () => gql`
    query getPageInfo($path: String!, $secret: String) {
      getPageInfo(path: $path, secret: $secret) {
        id
        path
        redirect {
          code
          redirect
        }
        type
        head {
          ...headData
        }
        breadcrumb {
          label
          path
        }
        breadcrumbStyle
        actions {
          name
          value
        }
        context {
          key
          data {
            ... on GroupVoucherCode {
              ...groupVoucher
            }
          }
        }
        pageData {
          ... on ProductPage {
            storyblok
            ...productPage
          }
          ... on ListPage {
            ...listPage
          }
          ... on Storyblok {
            storyblok
          }
        }
      }
    }
    ${HeadDataFragment}
    ${ProductPageFragment}
    ${ListPageFragment}
    ${GroupVoucherFragment}
  `,
  ...superpayQueries
};

export default queries;
